import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'Dashboard',
    component: () => import('@/pages/DashboardPage.vue'),
  },
  {
    path: '/feeds',
    name: 'Feeds',
    component: () => import('@/pages/FeedsPage.vue'),
  },
  {
    path: '/feeds/:id',
    name: 'FeedDetail',
    component: () => import('@/pages/FeedDetailPage.vue'),
  },
  {
    path: '/tags',
    name: 'Tags',
    component: () => import('@/pages/TagsPage.vue'),
  },
  {
    path: '/lists',
    name: 'Lists',
    component: () => import('@/pages/ListsPage.vue'),
  },
  {
    path: '/lists/:id',
    name: 'ListDetail',
    component: () => import('@/pages/ListDetailPage.vue'),
  },
  {
    path: '/items',
    name: 'Items',
    component: () => import('@/pages/ItemsPage.vue'),
  },
  {
    path: '/items/:id',
    name: 'ItemDetail',
    component: () => import('@/pages/ItemDetailPage.vue'),
  },
  {
    path: '/workflows',
    name: 'Workflows',
    component: () => import('@/pages/WorkflowsPage.vue'),
  },
  {
    path: '/workflows/:id',
    name: 'WorkflowDetail',
    component: () => import('@/pages/WorkflowDetailPage.vue'),
  },
  {
    path: '/podcasts',
    name: 'Podcasts',
    component: () => import('@/pages/PodcastsPage.vue'),
  },
  {
    path: '/podcasts/:id',
    name: 'PodcastDetail',
    component: () => import('@/pages/PodcastDetailPage.vue'),
  },
  {
    path: '/search',
    name: 'Search',
    component: () => import('@/pages/SearchPage.vue'),
  },
  {
    path: '/activity',
    name: 'Activity',
    component: () => import('@/pages/ActivityPage.vue'),
  },
  {
    path: '/jobs',
    name: 'Jobs',
    component: () => import('@/pages/JobsPage.vue'),
  },
  {
    path: '/admin',
    name: 'Admin',
    component: () => import('@/pages/AdminPage.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/settings',
    name: 'Settings',
    component: () => import('@/pages/SettingsPage.vue'),
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/pages/LoginPage.vue'),
    meta: { public: true },
  },
  {
    path: '/s/:token',
    name: 'SharedItem',
    component: () => import('@/pages/SharedItemPage.vue'),
    meta: { public: true },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/pages/NotFoundPage.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

// Navigation guard: ensure the user has an OIDC session before visiting any
// non-public route, and gate admin-only routes on role.
router.beforeEach(async (to) => {
  if (to.meta.public) {
    return true
  }

  const authStore = useAuthStore()

  // First navigation: kick off the session check if it hasn't happened yet.
  if (authStore.isLoading) {
    await authStore.initialize()
  }

  if (!authStore.isAuthenticated) {
    // A ?login_error from the callback, a 503 from the session probe, or a
    // tripped loop guard: show the sign-in page with the message instead of
    // bouncing straight back into the login that just failed.
    if (authStore.loginError) {
      return { name: 'Login', query: { return_to: to.fullPath } }
    }
    // No session — bounce to the OIDC login flow with the requested path as
    // return_to (a same-origin PATH, never a full href). The API sends the
    // browser back there after the callback. The per-tab loop guard refuses
    // after two automatic attempts; then we land on the sign-in page.
    if (!authStore.login(to.fullPath)) {
      return { name: 'Login', query: { return_to: to.fullPath } }
    }
    return false
  }

  if (to.meta.requiresAdmin && !authStore.isAdmin) {
    return { name: 'Dashboard' }
  }

  return true
})

export default router
